import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Divider, Grid, IconButton, InputAdornment,
  Link, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import {
  ArrowLeft, BookOpen, Check, CheckCircle2, ChevronRight, Copy, ExternalLink, FileText,
  HelpCircle, LifeBuoy, LogIn, LogOut, MessageSquare, Paperclip, Search, Send, ShieldCheck,
  ThumbsDown, ThumbsUp, Ticket, Upload, User, UserCheck, X, Eye, EyeOff, Lock, Mail, KeyRound,
} from "lucide-react";
import { portalApi } from "../api";

const Ctx = createContext(null);
const usePortal = () => useContext(Ctx);

const errText = (err, fallback) => err?.response?.data?.message || fallback;
const sessionKey = (slug) => `jt-portal:${slug}`;
const readSession = (slug) => {
  try {
    return JSON.parse(localStorage.getItem(sessionKey(slug)) || "null");
  } catch {
    return null;
  }
};
const writeSession = (slug, v) => {
  try {
    if (v) localStorage.setItem(sessionKey(slug), JSON.stringify(v));
    else localStorage.removeItem(sessionKey(slug));
  } catch {
    /* private mode */
  }
};

const fileSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
const fmt = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const STATUS_COLORS = {
  resolved: ["#ECFDF3", "#067647"],
  closed: ["#F2F4F7", "#344054"],
  pending: ["#FFFAEB", "#B54708"],
  open: ["#EFF8FF", "#175CD3"],
  new: ["#FDF2FA", "#C11574"],
};

// ---------------------------------------------------------------------------
// Shell & Layout
function PortalShell({ children }) {
  const { slug: routeSlug } = useParams();
  const slug = routeSlug || "justwalk-test";
  const location = useLocation();
  const tokenFromUrl = useMemo(() => new URLSearchParams(location.search).get("token") || "", [location.search]);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const [session, setSessionState] = useState(() => readSession(slug));

  // Verify and refresh support portal session
  useEffect(() => {
    if (session?.token) {
      portalApi.me(slug, session.token)
        .then((res) => {
          if (res?.user && (!session.user || res.user.email !== session.email)) {
            setSessionState((prev) => ({ ...prev, user: res.user, email: res.user.email }));
          }
        })
        .catch((err) => {
          if (err?.response?.status === 401) {
            setSession(null);
          }
        });
    }
  }, [slug, session?.token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    portalApi.config(slug, tokenFromUrl)
      .then(setConfig)
      .catch((err) => setError(errText(err, "This help center is not available")));
  }, [slug, tokenFromUrl]);

  useEffect(() => {
    if (config?.title) {
      document.title = config.company ? `${config.title} · ${config.company}` : config.title;
    }
  }, [config]);

  const setSession = useCallback((v) => {
    writeSession(slug, v);
    setSessionState(v);
  }, [slug]);

  const value = useMemo(
    () => (config ? { slug, config, session, setSession, base: `/support/${slug}` } : null),
    [slug, config, session, setSession]
  );

  if (error) {
    return (
      <Centered>
        <Alert severity="error">{error}</Alert>
      </Centered>
    );
  }

  if (!value) {
    return (
      <Centered>
        <CircularProgress size={30} sx={{ color: "#D946EF" }} />
      </Centered>
    );
  }

  const isDark = (config.theme || "dark") === "dark";
  const brandColor = config.color || (isDark ? "#D946EF" : "#6366F1");

  return (
    <Ctx.Provider value={value}>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: isDark ? "#0C0A17" : "#F8FAFC",
          color: isDark ? "#F8FAFC" : "#101828",
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          transition: "background-color 0.3s ease",
        }}
      >
        {/* Navigation Header */}
        <Box
          component="header"
          sx={{
            bgcolor: isDark ? "#140F26" : brandColor,
            borderBottom: isDark ? "1px solid #281F47" : "none",
            color: "#fff",
            position: "sticky",
            top: 0,
            zIndex: 100,
            boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.5)" : "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <Container maxWidth="md" sx={{ py: 1.5, px: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Link component={RouterLink} to={value.base} underline="none" sx={{ color: "#fff", display: "flex", alignItems: "center", gap: 1.25, mr: "auto" }}>
                {config.logoUrl ? (
                  <Box component="img" src={config.logoUrl} alt={config.title} sx={{ height: 32, maxWidth: 120, objectFit: "contain" }} />
                ) : (
                  <Box sx={{ width: 34, height: 34, borderRadius: "10px", bgcolor: isDark ? "#D946EF" : "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <LifeBuoy size={20} color="#fff" />
                  </Box>
                )}
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", lineHeight: 1.2 }}>{config.title || "Help Center"}</Typography>
                  {config.company && <Typography sx={{ fontSize: "0.75rem", opacity: 0.85 }}>{config.company}</Typography>}
                </Box>
              </Link>

              {config.allowTicketForm && (
                <Button component={RouterLink} to={`${value.base}/new`} size="small" sx={navBtn} startIcon={<FileText size={15} />}>
                  Submit a ticket
                </Button>
              )}
              <Button component={RouterLink} to={`${value.base}/tickets`} size="small" sx={navBtn} startIcon={<Ticket size={15} />}>
                My tickets
              </Button>

              {session?.token ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip
                    size="small"
                    avatar={<UserCheck size={14} color="#10B981" />}
                    label={session.user?.name || session.email}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.15)",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.78rem",
                      height: 28,
                    }}
                  />
                  <IconButton
                    size="small"
                    title="Sign Out of Support Portal"
                    onClick={() => {
                      portalApi.logout(slug).catch(() => {});
                      setSession(null);
                    }}
                    sx={{ color: "rgba(255,255,255,0.85)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.15)" } }}
                  >
                    <LogOut size={16} />
                  </IconButton>
                </Stack>
              ) : (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Button component={RouterLink} to={`${value.base}/login`} size="small" sx={navBtn} startIcon={<LogIn size={15} />}>
                    Log In
                  </Button>
                  <Button
                    component={RouterLink}
                    to={`${value.base}/register`}
                    size="small"
                    sx={{
                      ...navBtn,
                      bgcolor: isDark ? "#D946EF" : "#fff",
                      color: isDark ? "#fff" : brandColor,
                      "&:hover": { bgcolor: isDark ? "#C026D3" : "#F1F5F9" },
                    }}
                  >
                    Register
                  </Button>
                </Stack>
              )}
            </Stack>
          </Container>
        </Box>

        {/* Content Container */}
        <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 }, px: 2 }}>
          {children}
        </Container>
      </Box>
    </Ctx.Provider>
  );
}

const navBtn = {
  color: "#fff",
  textTransform: "none",
  fontWeight: 700,
  borderRadius: "8px",
  bgcolor: "rgba(255,255,255,0.12)",
  "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
};
const Centered = ({ children }) => <Box sx={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>{children}</Box>;
const BackLink = ({ to, children }) => {
  const { config } = usePortal();
  const isDark = (config?.theme || "dark") === "dark";
  return (
    <Link component={RouterLink} to={to} underline="hover" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 2.5, fontSize: "0.85rem", color: isDark ? "#A5B4FC" : "#475467" }}>
      <ArrowLeft size={16} />{children}
    </Link>
  );
};

// ---------------------------------------------------------------------------
// HOME PAGE (Knowledge Base & Quick Actions)
function PortalHome() {
  const { slug, config, base } = usePortal();
  const isDark = (config?.theme || "dark") === "dark";
  const [q, setQ] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    if (!config.showKnowledgeBase) return undefined;
    const t = setTimeout(() => {
      portalApi.articles(slug, { q: q || undefined, categoryId: categoryId || undefined })
        .then(setArticles)
        .catch(() => setArticles([]));
    }, 250);
    return () => clearTimeout(t);
  }, [slug, q, categoryId, config.showKnowledgeBase]);

  const catName = (id) => config.categories?.find((c) => String(c._id) === String(id))?.name;

  return (
    <Box>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography component="h1" sx={{ fontSize: { xs: "1.75rem", md: "2.3rem" }, fontWeight: 900, letterSpacing: "-0.02em", color: isDark ? "#F8FAFC" : "#0F172A" }}>
          {config.welcome || "How can we help you?"}
        </Typography>
        <Typography sx={{ mt: 1, fontSize: "1rem", color: isDark ? "#94A3B8" : "#64748B" }}>
          Search our knowledge base or submit a support ticket to our team.
        </Typography>

        {config.showKnowledgeBase && (
          <TextField
            fullWidth
            placeholder="Search articles, guides, and FAQs…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{
              mt: 3,
              maxWidth: 580,
              mx: "auto",
              "& .MuiOutlinedInput-root": {
                bgcolor: isDark ? "#171228" : "#fff",
                color: isDark ? "#fff" : "inherit",
                borderRadius: "14px",
                borderColor: isDark ? "#2E2452" : "#CBD5E1",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color={isDark ? "#A855F7" : "#64748B"} />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Box>

      {/* Action Cards */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 4 }}>
        {config.allowTicketForm && (
          <ActionCard
            to={`${base}/new`}
            icon={<FileText size={24} />}
            title="Submit a ticket"
            text="Describe your issue and our support engineers will get back to you promptly."
            isDark={isDark}
            color={config.color}
          />
        )}
        <ActionCard
          to={`${base}/tickets`}
          icon={<MessageSquare size={24} />}
          title="Track your tickets"
          text="Follow your support conversations, view ticket status, and send replies."
          isDark={isDark}
          color={config.color}
        />
      </Stack>

      {/* Knowledge Base */}
      {config.showKnowledgeBase && (
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", mb: 1.5, color: isDark ? "#E2E8F0" : "#1E293B" }}>
            Knowledge Base Articles
          </Typography>
          {config.categories?.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Chip label="All Topics" onClick={() => setCategoryId("")} color={!categoryId ? "primary" : "default"} variant={!categoryId ? "filled" : "outlined"} />
              {config.categories.map((c) => (
                <Chip key={c._id} label={c.name} onClick={() => setCategoryId(c._id)} color={categoryId === c._id ? "primary" : "default"} variant={categoryId === c._id ? "filled" : "outlined"} />
              ))}
            </Stack>
          )}

          <Paper
            variant="outlined"
            sx={{
              borderRadius: "14px",
              overflow: "hidden",
              bgcolor: isDark ? "#161226" : "#fff",
              borderColor: isDark ? "#2E2452" : "#E2E8F0",
            }}
          >
            {!articles ? (
              <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>
            ) : !articles.length ? (
              <Box sx={{ p: 4, textAlign: "center", color: isDark ? "#94A3B8" : "#64748B" }}>
                <BookOpen size={24} />
                <Typography sx={{ mt: 1, fontSize: "0.95rem" }}>{q ? "No articles match your search." : "No published articles available yet."}</Typography>
              </Box>
            ) : (
              articles.map((a, i) => (
                <Box key={a._id}>
                  {i > 0 && <Divider sx={{ borderColor: isDark ? "#281F47" : "#F1F5F9" }} />}
                  <Link
                    component={RouterLink}
                    to={`${base}/articles/${a._id}`}
                    underline="none"
                    sx={{
                      display: "block",
                      p: 2.25,
                      color: "inherit",
                      "&:hover": { bgcolor: isDark ? "#1E1838" : "#F8FAFC" },
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: isDark ? "#F8FAFC" : "#0F172A", fontSize: "0.98rem" }}>{a.title}</Typography>
                    <Typography sx={{ fontSize: "0.85rem", color: isDark ? "#94A3B8" : "#64748B", mt: 0.5 }}>{a.excerpt}</Typography>
                    {catName(a.categoryId) && (
                      <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#A855F7" : "#6366F1", mt: 0.75, fontWeight: 600 }}>{catName(a.categoryId)}</Typography>
                    )}
                  </Link>
                </Box>
              ))
            )}
          </Paper>
        </Box>
      )}

      {/* Support Contact Info Footer */}
      {(config.supportEmail || config.supportPhone || config.supportHours) && (
        <Box sx={{ mt: 5, p: 2.5, borderRadius: "12px", bgcolor: isDark ? "#140F26" : "#F1F5F9", textAlign: "center", border: isDark ? "1px solid #281F47" : "none" }}>
          <Typography sx={{ fontSize: "0.85rem", color: isDark ? "#94A3B8" : "#475467" }}>
            Need immediate help?
            {config.supportEmail && <span> Email us at <strong>{config.supportEmail}</strong> · </span>}
            {config.supportPhone && <span> Call <strong>{config.supportPhone}</strong> · </span>}
            {config.supportHours && <span> Hours: {config.supportHours}</span>}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

const ActionCard = ({ to, icon, title, text, isDark, color }) => (
  <Paper
    component={RouterLink}
    to={to}
    variant="outlined"
    sx={{
      flex: 1,
      p: 3,
      borderRadius: "16px",
      textDecoration: "none",
      color: "inherit",
      bgcolor: isDark ? "#161226" : "#fff",
      borderColor: isDark ? "#281F47" : "#E2E8F0",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: color || "#D946EF",
        transform: "translateY(-2px)",
        boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.4)" : "0 8px 24px rgba(0,0,0,0.06)",
      },
    }}
  >
    <Box sx={{ color: color || "#D946EF", mb: 1.5 }}>{icon}</Box>
    <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: isDark ? "#F8FAFC" : "#0F172A" }}>{title}</Typography>
    <Typography sx={{ fontSize: "0.86rem", color: isDark ? "#94A3B8" : "#64748B", mt: 0.5 }}>{text}</Typography>
  </Paper>
);

// ---------------------------------------------------------------------------
// ARTICLE DETAIL
function PortalArticle() {
  const { slug, base, config } = usePortal();
  const isDark = (config?.theme || "dark") === "dark";
  const { articleId } = useParams();
  const [a, setA] = useState(null);
  const [error, setError] = useState("");
  const [voted, setVoted] = useState(false);

  useEffect(() => {
    portalApi.article(slug, articleId).then(setA).catch((err) => setError(errText(err, "Article not found")));
  }, [slug, articleId]);

  const vote = async (helpful) => {
    setVoted(true);
    try {
      await portalApi.feedback(slug, articleId, helpful);
    } catch {
      /* ignore */
    }
  };

  return (
    <Box>
      <BackLink to={base}>All articles</BackLink>
      {error ? <Alert severity="error">{error}</Alert> : !a ? <CircularProgress size={24} /> : (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: "16px",
            bgcolor: isDark ? "#161226" : "#fff",
            borderColor: isDark ? "#281F47" : "#E2E8F0",
          }}
        >
          <Typography component="h1" sx={{ fontSize: "1.6rem", fontWeight: 800, mb: 0.5, color: isDark ? "#F8FAFC" : "#0F172A" }}>{a.title}</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: isDark ? "#94A3B8" : "#64748B", mb: 3 }}>Updated {fmt(a.updatedAt)}</Typography>
          <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 1.8, color: isDark ? "#CBD5E1" : "#334155", fontSize: "0.95rem" }}>{a.body}</Typography>
          <Divider sx={{ my: 3, borderColor: isDark ? "#281F47" : "#E2E8F0" }} />
          {voted ? (
            <Typography sx={{ fontSize: "0.9rem", color: "#10B981", fontWeight: 700 }}>Thanks for your feedback!</Typography>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography sx={{ fontSize: "0.9rem", mr: 1, color: isDark ? "#94A3B8" : "#475467" }}>Was this article helpful?</Typography>
              <Button size="small" variant="outlined" startIcon={<ThumbsUp size={14} />} onClick={() => vote(true)} sx={{ textTransform: "none" }}>Yes</Button>
              <Button size="small" variant="outlined" startIcon={<ThumbsDown size={14} />} onClick={() => vote(false)} sx={{ textTransform: "none" }}>No</Button>
            </Stack>
          )}
        </Paper>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TICKET CREATION FORM (Dark Theme Reference & Dynamic Config)
function PortalNewTicket() {
  const { slug, config, base, session, setSession } = usePortal();
  const navigate = useNavigate();
  const isDark = (config?.theme || "dark") === "dark";

  const isAuth = Boolean(session?.token);
  const [authMode, setAuthMode] = useState(() => (isAuth ? "authenticated" : "guest"));

  // Form state
  const [formValues, setFormValues] = useState(() => ({
    email: session?.email || "",
    requester: session?.email || "",
    name: session?.user?.name || "",
    phone: "",
    accountName: "",
    subject: "",
    description: "",
    isAdmin: "",
    helpTopic: "",
    impactedUsers: "",
    country: "",
    categoryId: config.defaultCategoryId || "",
    priorityId: config.defaultPriorityId || "",
    website: "", // honeypot
  }));

  const [files, setFiles] = useState([]);
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  // Sync session details if logged in
  useEffect(() => {
    if (session?.token && authMode === "authenticated") {
      setFormValues((prev) => ({
        ...prev,
        email: session.email || prev.email || "",
        requester: session.email || prev.requester || prev.email || "",
        name: session.user?.name || prev.name,
      }));
    }
  }, [session, authMode]);

  const fields = useMemo(() => {
    const list = Array.isArray(config.fields) && config.fields.length ? config.fields : [];
    // Filter fields according to current access mode
    return list.filter((f) => {
      if (f.visibility === "guest" && authMode !== "guest") return false;
      if (f.visibility === "authenticated" && authMode !== "authenticated") return false;
      return true;
    });
  }, [config.fields, authMode]);

  const handleChange = (key, val) => {
    setFormValues((prev) => {
      const next = { ...prev, [key]: val };
      if (key === "requester") next.email = val;
      if (key === "email") next.requester = val;
      return next;
    });
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const maxFiles = config.maxAttachments || 5;
    const maxMb = config.maxFileSizeMb || 20;

    const valid = [];
    for (const f of selected) {
      if (f.size > maxMb * 1024 * 1024) {
        setError(`File "${f.name}" exceeds the maximum limit of ${maxMb} MB.`);
        return;
      }
      valid.push(f);
    }

    if (files.length + valid.length > maxFiles) {
      setError(`You can upload at most ${maxFiles} files per ticket.`);
      return;
    }
    setError("");
    setFiles((prev) => [...prev, ...valid]);
    e.target.value = null;
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");

    // Validate CAPTCHA for guests
    if (config.requireCaptcha && authMode === "guest" && !captchaChecked) {
      setError("Please complete the robot verification checkbox.");
      setBusy(false);
      return;
    }

    try {
      const emailVal = formValues.email || formValues.requester || "";
      const payload = {
        ...formValues,
        email: emailVal,
        requester: emailVal,
        captchaToken: captchaChecked ? "verified_human" : "",
        captchaVerified: captchaChecked,
      };

      const authToken = authMode === "authenticated" && session?.token ? session.token : null;
      const res = await portalApi.createTicket(slug, payload, files, authToken);
      setDone(res);
    } catch (err) {
      setError(errText(err, "Could not submit your ticket. Please check the fields and try again."));
    } finally {
      setBusy(false);
    }
  };

  if (!config.allowTicketForm) {
    return <Alert severity="info">This help center does not accept new tickets online.</Alert>;
  }

  // SUCCESS CONFIRMATION SCREEN
  if (done) {
    const trackingFullUrl = done.guestToken
      ? `${window.location.origin}/support/${slug}/tickets/guest/${done.guestToken}`
      : `${window.location.origin}/support/${slug}/tickets`;

    return (
      <Paper
        variant="outlined"
        sx={{
          maxWidth: 620,
          mx: "auto",
          p: { xs: 3, md: 5 },
          textAlign: "center",
          borderRadius: "20px",
          bgcolor: isDark ? "#171228" : "#fff",
          borderColor: isDark ? "#2E2452" : "#E2E8F0",
          boxShadow: isDark ? "0 10px 40px rgba(0,0,0,0.6)" : "0 10px 30px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "#10B9811A", color: "#10B981", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
          <CheckCircle2 size={36} />
        </Box>
        <Typography component="h1" sx={{ fontSize: "1.5rem", fontWeight: 800, color: isDark ? "#F8FAFC" : "#0F172A", mb: 1 }}>
          {config.successTitle || "Your support request has been created successfully."}
        </Typography>

        {done.key && (
          <Box sx={{ my: 2.5, p: 2, bgcolor: isDark ? "#211A38" : "#F8FAFC", borderRadius: "12px", border: isDark ? "1px solid #362B5E" : "1px solid #E2E8F0" }}>
            <Typography sx={{ fontSize: "0.85rem", color: isDark ? "#94A3B8" : "#64748B", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>
              Ticket Reference Number
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mt: 0.5 }}>
              <Typography sx={{ fontSize: "1.6rem", fontWeight: 900, color: isDark ? "#D946EF" : "#6366F1", fontFamily: "monospace" }}>
                #{done.key}
              </Typography>
              <IconButton size="small" onClick={() => { navigator.clipboard?.writeText(done.key); }}>
                <Copy size={16} color={isDark ? "#A855F7" : "#64748B"} />
              </IconButton>
            </Stack>
          </Box>
        )}

        <Typography sx={{ color: isDark ? "#CBD5E1" : "#475467", fontSize: "0.92rem", lineHeight: 1.6, mb: 3 }}>
          {config.successMessage || `We have received your ticket and our team is reviewing it. We will reply to ${formValues.email}.`}
        </Typography>

        {done.guestToken && (
          <Box sx={{ mb: 3, p: 2, bgcolor: isDark ? "#120E22" : "#EFF6FF", borderRadius: "12px", border: isDark ? "1px solid #281F47" : "1px solid #BFDBFE", textAlign: "left" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: isDark ? "#F8FAFC" : "#1E40AF", mb: 0.5 }}>
              Secure Guest Tracking Link
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: isDark ? "#94A3B8" : "#3B82F6", mb: 1.5 }}>
              Bookmark or copy this private link to check real-time status and post replies without signing up:
            </Typography>
            <Box sx={{ p: 1, bgcolor: isDark ? "#1A152E" : "#DBEAFE", borderRadius: "6px", fontFamily: "monospace", fontSize: "0.75rem", wordBreak: "break-all", color: isDark ? "#CBD5E1" : "#1E3A8A" }}>
              {trackingFullUrl}
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button size="small" variant="outlined" startIcon={<Copy size={14} />} onClick={() => navigator.clipboard?.writeText(trackingFullUrl)}>
                Copy Access Link
              </Button>
              <Button size="small" variant="contained" sx={{ bgcolor: isDark ? "#D946EF" : "#2563EB", "&:hover": { bgcolor: isDark ? "#C026D3" : "#1D4ED8" } }} onClick={() => navigate(`/support/${slug}/tickets/guest/${done.guestToken}`)}>
                Track Ticket Now
              </Button>
            </Stack>
          </Box>
        )}

        <Stack direction="row" spacing={1.5} justifyContent="center">
          <Button component={RouterLink} to={base} sx={{ color: isDark ? "#A5B4FC" : "#475467", textTransform: "none" }}>
            Back to Help Center
          </Button>
          {!done.guestToken && (
            <Button component={RouterLink} to={`${base}/tickets`} variant="contained" sx={primaryPill(config.color)}>
              View My Tickets
            </Button>
          )}
        </Stack>
      </Paper>
    );
  }

  // TICKET FORM (Reference dark UI: dark container, purple fields, pill buttons, reCAPTCHA)
  return (
    <Box>
      <BackLink to={base}>Help center</BackLink>

      <Paper
        variant="outlined"
        sx={{
          maxWidth: 680,
          mx: "auto",
          p: { xs: 2.5, sm: 4 },
          borderRadius: "20px",
          bgcolor: isDark ? "#151026" : "#FFFFFF",
          borderColor: isDark ? "#281F47" : "#E2E8F0",
          boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.5)" : "0 8px 30px rgba(0,0,0,0.06)",
        }}
      >
        <Typography component="h1" sx={{ fontSize: "1.45rem", fontWeight: 800, color: isDark ? "#F8FAFC" : "#0F172A", mb: 0.5 }}>
          {config.formTitle || "Submit a ticket"}
        </Typography>
        {config.formDescription && (
          <Typography sx={{ fontSize: "0.86rem", color: isDark ? "#94A3B8" : "#64748B", mb: 2 }}>
            {config.formDescription}
          </Typography>
        )}

        {/* Access Mode Banner */}
        <Box sx={{ mb: 3, p: 1.5, borderRadius: "10px", bgcolor: isDark ? "#1E1838" : "#F1F5F9", border: isDark ? "1px solid #33285C" : "1px solid #E2E8F0" }}>
          {session?.token ? (
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
              <Stack direction="row" alignItems="center" spacing={1}>
                <UserCheck size={18} color="#10B981" />
                <Typography sx={{ fontSize: "0.82rem", color: isDark ? "#E2E8F0" : "#334155" }}>
                  Signed in as <strong>{session.user?.name || session.email}</strong> ({session.email})
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  onClick={() => setAuthMode((m) => (m === "authenticated" ? "guest" : "authenticated"))}
                  sx={{ textTransform: "none", fontSize: "0.78rem", color: isDark ? "#D946EF" : "#6366F1" }}
                >
                  {authMode === "authenticated" ? "Submit as Guest Instead" : "Submit as Logged In"}
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    portalApi.logout(slug).catch(() => {});
                    setSession(null);
                    setAuthMode("guest");
                  }}
                  sx={{ textTransform: "none", fontSize: "0.78rem", color: "#EF4444" }}
                >
                  Sign Out
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
              <Stack direction="row" alignItems="center" spacing={1}>
                <User size={16} color={isDark ? "#A855F7" : "#64748B"} />
                <Typography sx={{ fontSize: "0.82rem", color: isDark ? "#94A3B8" : "#475467" }}>
                  Submitting as Guest (No account needed)
                </Typography>
              </Stack>
              <Button
                component={RouterLink}
                to={`${base}/login?returnTo=${encodeURIComponent(`${base}/new`)}`}
                sx={{ textTransform: "none", fontSize: "0.78rem", color: isDark ? "#D946EF" : "#6366F1", fontWeight: 700 }}
              >
                Have an account? Log In
              </Button>
            </Stack>
          )}
        </Box>

        {/* Dynamic Form */}
        <Box component="form" onSubmit={submit}>
          <Stack spacing={2.25}>
            {fields.map((f) => {
              const val = formValues[f.key] ?? "";
              const labelWithAsterisk = (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: isDark ? "#E2E8F0" : "#334155" }}>
                    {f.label}
                  </Typography>
                  {f.required && <Typography component="span" sx={{ color: "#D946EF", fontWeight: 700 }}>*</Typography>}
                  {f.tooltip && (
                    <Tooltip title={f.tooltip} arrow>
                      <Box component="span" sx={{ display: "inline-flex", color: isDark ? "#A855F7" : "#64748B", cursor: "pointer", ml: 0.5 }}>
                        <HelpCircle size={14} />
                      </Box>
                    </Tooltip>
                  )}
                </Stack>
              );

              // Field Styling for Dark / Light theme
              const fieldSx = {
                "& .MuiOutlinedInput-root": {
                  bgcolor: isDark ? "#120E22" : "#FFFFFF",
                  color: isDark ? "#F8FAFC" : "inherit",
                  borderRadius: "10px",
                  borderColor: isDark ? "#2C234D" : "#CBD5E1",
                  "&:hover fieldset": { borderColor: isDark ? "#A855F7" : "#94A3B8" },
                  "&.Mui-focused fieldset": { borderColor: isDark ? "#D946EF" : "#6366F1" },
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.88rem",
                  py: 1.25,
                },
              };

              // Select / Dropdown
              if (f.type === "select") {
                return (
                  <Box key={f.id || f.key}>
                    {labelWithAsterisk}
                    <TextField
                      select
                      fullWidth
                      size="small"
                      required={f.required}
                      value={val}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                      sx={{ mt: 0.75, ...fieldSx }}
                    >
                      <MenuItem value="">{f.placeholder || "…"}</MenuItem>
                      {f.options?.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                );
              }

              // Textarea / Rich Text style
              if (f.type === "textarea") {
                return (
                  <Box key={f.id || f.key}>
                    {labelWithAsterisk}
                    {/* Rich text formatting bar preview (matching reference screenshot) */}
                    <Box
                      sx={{
                        mt: 0.75,
                        p: 0.75,
                        bgcolor: isDark ? "#17122B" : "#F8FAFC",
                        border: isDark ? "1px solid #2C234D" : "1px solid #E2E8F0",
                        borderBottom: "none",
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                        display: "flex",
                        gap: 1.25,
                        alignItems: "center",
                        color: isDark ? "#94A3B8" : "#64748B",
                        fontSize: "0.78rem",
                        fontFamily: "serif",
                      }}
                    >
                      <Box sx={{ fontWeight: 800, cursor: "pointer", px: 0.5 }}>B</Box>
                      <Box sx={{ fontStyle: "italic", cursor: "pointer", px: 0.5 }}>I</Box>
                      <Box sx={{ textDecoration: "underline", cursor: "pointer", px: 0.5 }}>U</Box>
                      <Box sx={{ textDecoration: "line-through", cursor: "pointer", px: 0.5 }}>S</Box>
                      <Divider orientation="vertical" flexItem sx={{ borderColor: isDark ? "#2C234D" : "#E2E8F0" }} />
                      <Box sx={{ cursor: "pointer", fontSize: "0.75rem" }}>≣</Box>
                      <Box sx={{ cursor: "pointer", fontSize: "0.75rem" }}>⋮≡</Box>
                      <Divider orientation="vertical" flexItem sx={{ borderColor: isDark ? "#2C234D" : "#E2E8F0" }} />
                      <Box sx={{ cursor: "pointer", fontSize: "0.75rem" }}>🔗</Box>
                      <Box sx={{ cursor: "pointer", fontSize: "0.75rem" }}>🖼️</Box>
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={5}
                      required={f.required}
                      placeholder={f.placeholder || "Please describe the case in detail..."}
                      value={val}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                      sx={{
                        ...fieldSx,
                        "& .MuiOutlinedInput-root": {
                          ...fieldSx["& .MuiOutlinedInput-root"],
                          borderTopLeftRadius: 0,
                          borderTopRightRadius: 0,
                        },
                      }}
                      inputProps={{ maxLength: 20000 }}
                    />
                  </Box>
                );
              }

              // Standard inputs (text, email, tel, number)
              return (
                <Box key={f.id || f.key}>
                  {labelWithAsterisk}
                  <TextField
                    fullWidth
                    size="small"
                    required={f.required}
                    type={f.type === "email" ? "email" : f.type === "tel" ? "tel" : f.type === "number" ? "number" : "text"}
                    placeholder={f.placeholder || ""}
                    value={val}
                    onChange={(e) => handleChange(f.key, e.target.value)}
                    sx={{ mt: 0.75, ...fieldSx }}
                  />
                </Box>
              );
            })}

            {/* Attach a file (Matching reference screenshot) */}
            {config.allowAttachments !== false && (
              <Box sx={{ pt: 0.5 }}>
                <Button
                  component="label"
                  size="small"
                  startIcon={<Paperclip size={16} />}
                  sx={{
                    color: isDark ? "#D946EF" : "#6366F1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    p: 0,
                    "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                  }}
                >
                  Attach a file
                  <input type="file" multiple hidden onChange={handleFileChange} />
                </Button>

                {files.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                    {files.map((file, i) => (
                      <Chip
                        key={file.name + i}
                        size="small"
                        icon={<Paperclip size={13} />}
                        label={`${file.name} (${fileSize(file.size)})`}
                        onDelete={() => handleRemoveFile(i)}
                        deleteIcon={<X size={13} />}
                        sx={{
                          bgcolor: isDark ? "#1E1838" : "#F1F5F9",
                          color: isDark ? "#E2E8F0" : "#334155",
                          border: isDark ? "1px solid #362B5E" : "1px solid #CBD5E1",
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            )}

            {/* Interactive Bot Verification / reCAPTCHA (Matching reference screenshot) */}
            {config.requireCaptcha && (
              <Box sx={{ pt: 1 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    p: 1.5,
                    px: 2,
                    borderRadius: "10px",
                    bgcolor: isDark ? "#120E22" : "#F8FAFC",
                    borderColor: isDark ? "#2C234D" : "#CBD5E1",
                    minWidth: 260,
                  }}
                >
                  <Box
                    onClick={() => setCaptchaChecked((c) => !c)}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "4px",
                      border: captchaChecked ? "2px solid #10B981" : isDark ? "2px solid #64748B" : "2px solid #CBD5E1",
                      bgcolor: captchaChecked ? "#10B981" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      mr: 1.5,
                      transition: "0.2s",
                    }}
                  >
                    {captchaChecked && <Check size={16} color="#fff" />}
                  </Box>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: isDark ? "#E2E8F0" : "#334155", mr: "auto" }}>
                    I'm not a robot
                  </Typography>
                  <Box sx={{ textAlign: "center", pl: 2, borderLeft: isDark ? "1px solid #281F47" : "1px solid #E2E8F0" }}>
                    <ShieldCheck size={20} color={isDark ? "#A855F7" : "#4F46E5"} />
                    <Typography sx={{ fontSize: "0.6rem", color: isDark ? "#94A3B8" : "#94A3B8", lineHeight: 1 }}>reCAPTCHA</Typography>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Honeypot field (hidden from real users, filled by bots) */}
            <Box aria-hidden="true" sx={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
              <TextField label="Website" tabIndex={-1} autoComplete="off" value={formValues.website} onChange={(e) => handleChange("website", e.target.value)} />
            </Box>

            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

            <Divider sx={{ my: 1, borderColor: isDark ? "#281F47" : "#E2E8F0" }} />

            {/* Submit and Cancel Pill Buttons (Matching reference screenshot) */}
            <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={busy}
                sx={{
                  bgcolor: "#D946EF",
                  background: "linear-gradient(135deg, #BE185D 0%, #D946EF 100%)",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: "0.88rem",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  borderRadius: "50px",
                  px: 4,
                  py: 1.25,
                  boxShadow: "0 4px 15px rgba(217, 70, 239, 0.4)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #9D174D 0%, #C026D3 100%)",
                    boxShadow: "0 6px 20px rgba(217, 70, 239, 0.6)",
                  },
                }}
                endIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {busy ? "Submitting…" : "SUBMIT"}
              </Button>

              <Button
                type="button"
                onClick={() => navigate(base)}
                sx={{
                  bgcolor: isDark ? "#221C3B" : "#F1F5F9",
                  color: isDark ? "#E2E8F0" : "#475467",
                  border: isDark ? "1px solid #3B3164" : "1px solid #CBD5E1",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  borderRadius: "50px",
                  px: 3,
                  py: 1.25,
                  "&:hover": {
                    bgcolor: isDark ? "#2C244C" : "#E2E8F0",
                  },
                }}
              >
                CANCEL
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}

const primaryPill = (color) => ({
  bgcolor: color || "#D946EF",
  color: "#fff",
  textTransform: "none",
  fontWeight: 700,
  borderRadius: "50px",
  px: 3,
  "&:hover": { bgcolor: color || "#C026D3" },
});

// ---------------------------------------------------------------------------
// GUEST TICKET DIRECT ACCESS & CONVERSATION VIEW
function PortalGuestTicket() {
  const { slug, config, base } = usePortal();
  const { token } = useParams();
  const isDark = (config?.theme || "dark") === "dark";

  const [t, setT] = useState(null);
  const [error, setError] = useState("");
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadTicket = useCallback(() => {
    if (!token) return;
    portalApi.guestTicket(slug, token)
      .then(setT)
      .catch((err) => setError(errText(err, "Ticket not found or link has expired.")));
  }, [slug, token]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  const handleDownload = async (file) => {
    try {
      const blob = await portalApi.guestFile(slug, token, file._id);
      const fileUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = fileUrl;
      a.download = file.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(fileUrl), 10000);
    } catch {
      setError("Could not download file.");
    }
  };

  const sendReply = async () => {
    if (!reply.trim() && !files.length) return;
    setBusy(true);
    try {
      const updated = await portalApi.guestReply(slug, token, reply, files);
      setT(updated);
      setReply("");
      setFiles([]);
    } catch (err) {
      setError(errText(err, "Could not send reply. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <BackLink to={base}>Help center</BackLink>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!t ? (!error && <Centered><CircularProgress size={28} sx={{ color: "#D946EF" }} /></Centered>) : (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: "18px",
            bgcolor: isDark ? "#161226" : "#fff",
            borderColor: isDark ? "#281F47" : "#E2E8F0",
          }}
        >
          {/* Header */}
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="flex-start" spacing={1.5} sx={{ mb: 2.5 }}>
            <Box>
              <Typography component="h1" sx={{ fontSize: "1.35rem", fontWeight: 800, color: isDark ? "#F8FAFC" : "#0F172A", wordBreak: "break-word" }}>
                {t.subject}
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", color: isDark ? "#94A3B8" : "#64748B", mt: 0.25 }}>
                #{t.key} · Submitted on {fmt(t.createdAt)} · Requester: {t.requester?.name || t.requester?.email}
              </Typography>
            </Box>
            <StatusChip status={t.status} statusType={t.statusType} />
          </Stack>

          {/* Properties bar */}
          {(t.category || t.priority || Object.keys(t.customFields || {}).length > 0) && (
            <Box sx={{ mb: 3, p: 1.5, borderRadius: "10px", bgcolor: isDark ? "#1E1838" : "#F8FAFC", border: isDark ? "1px solid #33285C" : "1px solid #E2E8F0" }}>
              <Grid container spacing={2}>
                {t.category && (
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: "0.72rem", color: isDark ? "#94A3B8" : "#64748B", textTransform: "uppercase", fontWeight: 700 }}>Category</Typography>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: isDark ? "#E2E8F0" : "#1E293B" }}>{t.category}</Typography>
                  </Grid>
                )}
                {t.priority && (
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: "0.72rem", color: isDark ? "#94A3B8" : "#64748B", textTransform: "uppercase", fontWeight: 700 }}>Priority</Typography>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: isDark ? "#E2E8F0" : "#1E293B" }}>{t.priority}</Typography>
                  </Grid>
                )}
                {t.customFields?.accountName && (
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: "0.72rem", color: isDark ? "#94A3B8" : "#64748B", textTransform: "uppercase", fontWeight: 700 }}>Account / Instance</Typography>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: isDark ? "#E2E8F0" : "#1E293B" }}>{t.customFields.accountName}</Typography>
                  </Grid>
                )}
                {t.customFields?.country && (
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: "0.72rem", color: isDark ? "#94A3B8" : "#64748B", textTransform: "uppercase", fontWeight: 700 }}>Country</Typography>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: isDark ? "#E2E8F0" : "#1E293B" }}>{t.customFields.country}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Conversation Thread */}
          <Typography sx={{ fontWeight: 800, fontSize: "1rem", mb: 2, color: isDark ? "#E2E8F0" : "#1E293B" }}>
            Conversation & Updates
          </Typography>

          <Stack spacing={2} sx={{ mb: 3 }}>
            {t.messages?.map((m) => {
              const isYou = m.from === "you";
              return (
                <Box key={m._id} sx={{ alignSelf: isYou ? "flex-end" : "flex-start", maxWidth: { xs: "100%", sm: "85%" } }}>
                  <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#94A3B8" : "#64748B", mb: 0.5, textAlign: isYou ? "right" : "left" }}>
                    {isYou ? "You" : m.name || "Support"} · {fmt(m.createdAt)}
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "14px",
                      bgcolor: isYou ? (isDark ? "#281D45" : "#EEF4FF") : isDark ? "#1B1530" : "#F8FAFC",
                      border: isDark ? "1px solid #36295C" : "1px solid #E2E8F0",
                      color: isDark ? "#F8FAFC" : "#101828",
                      fontSize: "0.9rem",
                      lineHeight: 1.6,
                      wordBreak: "break-word",
                    }}
                  >
                    {m.html ? <Box dangerouslySetInnerHTML={{ __html: m.html }} /> : <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "0.9rem" }}>{m.content}</Typography>}

                    {m.attachments?.length > 0 && (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                        {m.attachments.map((file) => (
                          <Chip
                            key={file._id}
                            size="small"
                            icon={<Paperclip size={13} />}
                            label={`${file.fileName} (${fileSize(file.size)})`}
                            onClick={() => handleDownload(file)}
                            sx={{
                              bgcolor: isDark ? "#120E22" : "#FFFFFF",
                              border: isDark ? "1px solid #3B2E64" : "1px solid #CBD5E1",
                              color: isDark ? "#CBD5E1" : "inherit",
                              cursor: "pointer",
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>

          {/* Reply Box */}
          {config.allowCustomerReplies !== false && (
            <Box sx={{ mt: 3, pt: 2, borderTop: isDark ? "1px solid #281F47" : "1px solid #E2E8F0" }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", mb: 1, color: isDark ? "#E2E8F0" : "#1E293B" }}>
                Send a reply
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                placeholder={["resolved", "closed"].includes(t.statusType) ? "Reply to reopen this ticket…" : "Type your message here…"}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: isDark ? "#120E22" : "#fff",
                    color: isDark ? "#fff" : "inherit",
                    borderRadius: "12px",
                    borderColor: isDark ? "#2C234D" : "#CBD5E1",
                  },
                }}
              />

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
                <Button component="label" size="small" startIcon={<Paperclip size={15} />} sx={{ textTransform: "none", color: isDark ? "#D946EF" : "#6366F1" }}>
                  Add attachment
                  <input type="file" multiple hidden onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                </Button>

                <Button
                  variant="contained"
                  onClick={sendReply}
                  disabled={busy || (!reply.trim() && !files.length)}
                  sx={{
                    bgcolor: "#D946EF",
                    borderRadius: "50px",
                    textTransform: "none",
                    fontWeight: 700,
                    px: 3,
                    "&:hover": { bgcolor: "#C026D3" },
                  }}
                  endIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Send size={15} />}
                >
                  {busy ? "Sending…" : "Send Reply"}
                </Button>
              </Stack>

              {files.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  {files.map((f, i) => (
                    <Chip key={f.name + i} size="small" label={f.name} onDelete={() => setFiles(files.filter((_, idx) => idx !== i))} />
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}

const StatusChip = ({ status, statusType }) => {
  const [bg, fg] = STATUS_COLORS[statusType] || ["#EEF4FF", "#3538CD"];
  return <Chip size="small" label={status || statusType || "Open"} sx={{ bgcolor: bg, color: fg, fontWeight: 800, height: 24, fontSize: "0.78rem" }} />;
};

const portalInputSx = (isDark) => ({
  "& .MuiOutlinedInput-root": {
    bgcolor: isDark ? "#120E22" : "#fff",
    color: isDark ? "#fff" : "inherit",
    borderRadius: "12px",
    borderColor: isDark ? "#281F47" : "#E2E8F0",
    "&:hover fieldset": {
      borderColor: isDark ? "#D946EF" : "#6366F1",
    },
  },
  "& .MuiInputLabel-root": {
    color: isDark ? "#94A3B8" : "#64748B",
  },
});

// ---------------------------------------------------------------------------
// INDEPENDENT PORTAL AUTHENTICATION PAGES

function PortalLogin({ returnTo: propReturnTo }) {
  const { slug, config, base, setSession } = usePortal();
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = (config?.theme || "dark") === "dark";
  const brandColor = config.color || (isDark ? "#D946EF" : "#6366F1");

  const queryReturnTo = new URLSearchParams(location.search).get("returnTo");
  const returnTo = propReturnTo || queryReturnTo || `${base}/tickets`;

  const [tab, setTab] = useState(0); // 0 = Email Link, 1 = Email OTP
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [magicSent, setMagicSent] = useState(false);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await portalApi.sendOtp(slug, email);
      setOtpSent(true);
      setResendCooldown(60);
    } catch (err) {
      setError(errText(err, "Could not send verification code. Please check your email and try again."));
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.trim().length < 6) {
      setError("Please enter the 6-digit verification code.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await portalApi.verifyOtp(slug, { email, otp: otp.trim() });
      setSession({ token: res.token, email: res.email, user: res.user });
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(errText(err, "Invalid or expired verification code."));
    } finally {
      setBusy(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await portalApi.requestLink(slug, email);
      setMagicSent(true);
    } catch (err) {
      setError(errText(err, "Could not send sign-in link"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <BackLink to={base}>Help center</BackLink>
      <Paper
        variant="outlined"
        sx={{
          maxWidth: 460,
          mx: "auto",
          p: { xs: 3, sm: 4 },
          borderRadius: "20px",
          bgcolor: isDark ? "#151026" : "#fff",
          borderColor: isDark ? "#281F47" : "#E2E8F0",
          boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.5)" : "0 8px 30px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              bgcolor: isDark ? "rgba(217,70,239,0.15)" : "rgba(99,102,241,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 1.5,
              color: brandColor,
            }}
          >
            <Lock size={24} />
          </Box>
          <Typography component="h1" sx={{ fontSize: "1.35rem", fontWeight: 800, color: isDark ? "#F8FAFC" : "#0F172A" }}>
            Sign In to Support Portal
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: isDark ? "#94A3B8" : "#64748B", mt: 0.5 }}>
            View and manage your support tickets
          </Typography>
        </Box>

        {/* Tab switch between Email Link & Email OTP */}
        <Stack direction="row" spacing={1} sx={{ p: 0.5, bgcolor: isDark ? "#1E1838" : "#F1F5F9", borderRadius: "10px", mb: 2.5 }}>
          <Button
            size="small"
            fullWidth
            onClick={() => { setTab(0); setError(""); }}
            sx={{
              borderRadius: "8px",
              fontWeight: 700,
              fontSize: "0.82rem",
              textTransform: "none",
              bgcolor: tab === 0 ? (isDark ? "#2A204C" : "#fff") : "transparent",
              color: tab === 0 ? (isDark ? "#F8FAFC" : "#0F172A") : (isDark ? "#94A3B8" : "#64748B"),
              boxShadow: tab === 0 ? "0 2px 4px rgba(0,0,0,0.08)" : "none",
            }}
          >
            Email Link
          </Button>
          <Button
            size="small"
            fullWidth
            onClick={() => { setTab(1); setError(""); }}
            sx={{
              borderRadius: "8px",
              fontWeight: 700,
              fontSize: "0.82rem",
              textTransform: "none",
              bgcolor: tab === 1 ? (isDark ? "#2A204C" : "#fff") : "transparent",
              color: tab === 1 ? (isDark ? "#F8FAFC" : "#0F172A") : (isDark ? "#94A3B8" : "#64748B"),
              boxShadow: tab === 1 ? "0 2px 4px rgba(0,0,0,0.08)" : "none",
            }}
          >
            Email OTP
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: "10px" }}>{error}</Alert>}

        {tab === 0 ? (
          <Box component="form" onSubmit={handleMagicLink}>
            {magicSent ? (
              <Alert severity="success" sx={{ borderRadius: "10px" }}>
                If <strong>{email}</strong> has tickets with us, we sent a secure sign-in link to your inbox. The link is valid for 30 minutes.
              </Alert>
            ) : (
              <Stack spacing={2}>
                <Typography sx={{ fontSize: "0.85rem", color: isDark ? "#94A3B8" : "#64748B" }}>
                  Enter the email address you used for support. We'll send you a passwordless sign-in link.
                </Typography>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mail size={16} color={isDark ? "#94A3B8" : "#64748B"} />
                      </InputAdornment>
                    ),
                  }}
                  sx={portalInputSx(isDark)}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={busy}
                  sx={{
                    bgcolor: brandColor,
                    borderRadius: "50px",
                    py: 1.2,
                    fontWeight: 700,
                    textTransform: "none",
                    "&:hover": { bgcolor: isDark ? "#C026D3" : "#4F46E5" },
                  }}
                >
                  {busy ? <CircularProgress size={20} color="inherit" /> : "Email me a sign-in link"}
                </Button>
              </Stack>
            )}
          </Box>
        ) : (
          <Box component="form" onSubmit={otpSent ? handleVerifyOtp : handleSendOtp}>
            {!otpSent ? (
              <Stack spacing={2}>
                <Typography sx={{ fontSize: "0.85rem", color: isDark ? "#94A3B8" : "#64748B" }}>
                  Enter your email to receive a 6-digit one-time code for instant access.
                </Typography>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mail size={16} color={isDark ? "#94A3B8" : "#64748B"} />
                      </InputAdornment>
                    ),
                  }}
                  sx={portalInputSx(isDark)}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={busy}
                  sx={{
                    bgcolor: brandColor,
                    borderRadius: "50px",
                    py: 1.2,
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: "0.92rem",
                    "&:hover": { bgcolor: isDark ? "#C026D3" : "#4F46E5" },
                  }}
                >
                  {busy ? <CircularProgress size={20} color="inherit" /> : "Send 6-Digit OTP"}
                </Button>
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Alert severity="success" sx={{ borderRadius: "10px" }}>
                  A 6-digit verification code has been sent to <strong>{email}</strong>.
                </Alert>
                <TextField
                  fullWidth
                  required
                  autoFocus
                  label="6-Digit OTP Code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="• • • • • •"
                  inputProps={{
                    maxLength: 6,
                    style: { textAlign: "center", letterSpacing: "8px", fontWeight: 700, fontSize: "1.4rem" },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <KeyRound size={20} color={isDark ? "#94A3B8" : "#64748B"} />
                      </InputAdornment>
                    ),
                  }}
                  sx={portalInputSx(isDark)}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={busy || otp.trim().length < 6}
                  sx={{
                    bgcolor: brandColor,
                    borderRadius: "50px",
                    py: 1.2,
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: "0.92rem",
                    "&:hover": { bgcolor: isDark ? "#C026D3" : "#4F46E5" },
                  }}
                >
                  {busy ? <CircularProgress size={20} color="inherit" /> : "Verify & Sign In"}
                </Button>

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 1 }}>
                  <Button
                    size="small"
                    onClick={() => { setOtpSent(false); setOtp(""); setError(""); }}
                    sx={{ textTransform: "none", fontSize: "0.82rem", color: isDark ? "#94A3B8" : "#64748B" }}
                  >
                    Change email
                  </Button>
                  <Button
                    size="small"
                    disabled={resendCooldown > 0 || busy}
                    onClick={handleSendOtp}
                    sx={{ textTransform: "none", fontSize: "0.82rem", fontWeight: 700, color: isDark ? "#D946EF" : brandColor }}
                  >
                    {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : "Resend code"}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

function PortalRegister() {
  const { slug, config, base, setSession } = usePortal();
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = (config?.theme || "dark") === "dark";
  const brandColor = config.color || (isDark ? "#D946EF" : "#6366F1");

  const returnTo = new URLSearchParams(location.search).get("returnTo") || `${base}/tickets`;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || name.trim().length < 2) {
      setError("Please enter your name (at least 2 characters).");
      return;
    }
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const res = await portalApi.register(slug, { name: name.trim(), email: email.trim(), password });
      setSession({ token: res.token, email: res.email, user: res.user });
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(errText(err, "Registration failed. Please check your information."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <BackLink to={base}>Help center</BackLink>
      <Paper
        variant="outlined"
        sx={{
          maxWidth: 480,
          mx: "auto",
          p: { xs: 3, sm: 4 },
          borderRadius: "20px",
          bgcolor: isDark ? "#151026" : "#fff",
          borderColor: isDark ? "#281F47" : "#E2E8F0",
          boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.5)" : "0 8px 30px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              bgcolor: isDark ? "rgba(217,70,239,0.15)" : "rgba(99,102,241,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 1.5,
              color: brandColor,
            }}
          >
            <UserCheck size={24} />
          </Box>
          <Typography component="h1" sx={{ fontSize: "1.35rem", fontWeight: 800, color: isDark ? "#F8FAFC" : "#0F172A" }}>
            Create Support Account
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: isDark ? "#94A3B8" : "#64748B", mt: 0.5 }}>
            Register to submit, track, and update all your support tickets
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: "10px" }}>{error}</Alert>}

        <Box component="form" onSubmit={handleRegister}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              required
              label="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <User size={16} color={isDark ? "#94A3B8" : "#64748B"} />
                  </InputAdornment>
                ),
              }}
              sx={portalInputSx(isDark)}
            />

            <TextField
              fullWidth
              required
              type="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Mail size={16} color={isDark ? "#94A3B8" : "#64748B"} />
                  </InputAdornment>
                ),
              }}
              sx={portalInputSx(isDark)}
            />

            <TextField
              fullWidth
              required
              type={showPassword ? "text" : "password"}
              label="Password (min. 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock size={16} color={isDark ? "#94A3B8" : "#64748B"} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword((p) => !p)} edge="end">
                      {showPassword ? <EyeOff size={16} color={isDark ? "#94A3B8" : "#64748B"} /> : <Eye size={16} color={isDark ? "#94A3B8" : "#64748B"} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={portalInputSx(isDark)}
            />

            <TextField
              fullWidth
              required
              type={showPassword ? "text" : "password"}
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock size={16} color={isDark ? "#94A3B8" : "#64748B"} />
                  </InputAdornment>
                ),
              }}
              sx={portalInputSx(isDark)}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={busy}
              sx={{
                bgcolor: brandColor,
                borderRadius: "50px",
                py: 1.25,
                fontWeight: 700,
                textTransform: "none",
                fontSize: "0.92rem",
                "&:hover": { bgcolor: isDark ? "#C026D3" : "#4F46E5" },
              }}
            >
              {busy ? <CircularProgress size={20} color="inherit" /> : "Create Account"}
            </Button>
          </Stack>

          <Divider sx={{ my: 2.5, borderColor: isDark ? "#281F47" : "#E2E8F0" }} />

          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.85rem", color: isDark ? "#94A3B8" : "#64748B" }}>
              Already have an account?{" "}
              <Link
                component={RouterLink}
                to={`${base}/login?returnTo=${encodeURIComponent(returnTo)}`}
                underline="hover"
                sx={{ color: isDark ? "#D946EF" : brandColor, fontWeight: 700 }}
              >
                Sign in here
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

function PortalForgotPassword() {
  const { slug, config, base } = usePortal();
  const isDark = (config?.theme || "dark") === "dark";
  const brandColor = config.color || (isDark ? "#D946EF" : "#6366F1");

  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await portalApi.forgotPassword(slug, email);
      setSent(true);
    } catch (err) {
      setError(errText(err, "Could not send password reset email."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <BackLink to={`${base}/login`}>Back to sign in</BackLink>
      <Paper
        variant="outlined"
        sx={{
          maxWidth: 460,
          mx: "auto",
          p: { xs: 3, sm: 4 },
          borderRadius: "20px",
          bgcolor: isDark ? "#151026" : "#fff",
          borderColor: isDark ? "#281F47" : "#E2E8F0",
          boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.5)" : "0 8px 30px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              bgcolor: isDark ? "rgba(217,70,239,0.15)" : "rgba(99,102,241,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 1.5,
              color: brandColor,
            }}
          >
            <KeyRound size={24} />
          </Box>
          <Typography component="h1" sx={{ fontSize: "1.35rem", fontWeight: 800, color: isDark ? "#F8FAFC" : "#0F172A" }}>
            Forgot Password
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: isDark ? "#94A3B8" : "#64748B", mt: 0.5 }}>
            Enter your email and we'll send you a link to reset your password.
          </Typography>
        </Box>

        {sent ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2.5, borderRadius: "10px" }}>
              If an account exists for <strong>{email}</strong>, we have sent password reset instructions to your inbox.
            </Alert>
            <Button
              component={RouterLink}
              to={`${base}/login`}
              fullWidth
              variant="outlined"
              sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700 }}
            >
              Return to Login
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: "10px" }}>{error}</Alert>}
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Mail size={16} color={isDark ? "#94A3B8" : "#64748B"} />
                    </InputAdornment>
                  ),
                }}
                sx={portalInputSx(isDark)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={busy}
                sx={{
                  bgcolor: brandColor,
                  borderRadius: "50px",
                  py: 1.2,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { bgcolor: isDark ? "#C026D3" : "#4F46E5" },
                }}
              >
                {busy ? <CircularProgress size={20} color="inherit" /> : "Send Reset Link"}
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

function PortalResetPassword() {
  const { slug, config, base } = usePortal();
  const location = useLocation();
  const isDark = (config?.theme || "dark") === "dark";
  const brandColor = config.color || (isDark ? "#D946EF" : "#6366F1");

  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const hashParams = new URLSearchParams(location.hash.replace(/^#/, ""));
    const queryParams = new URLSearchParams(location.search);
    const t = hashParams.get("token") || queryParams.get("token") || "";
    setToken(t);
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setError("Reset token is missing. Please click the link in your email again.");
      return;
    }
    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      await portalApi.resetPassword(slug, { token, password });
      setSuccess(true);
    } catch (err) {
      setError(errText(err, "Password reset link is invalid or has expired. Please request a new one."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <BackLink to={`${base}/login`}>Back to sign in</BackLink>
      <Paper
        variant="outlined"
        sx={{
          maxWidth: 460,
          mx: "auto",
          p: { xs: 3, sm: 4 },
          borderRadius: "20px",
          bgcolor: isDark ? "#151026" : "#fff",
          borderColor: isDark ? "#281F47" : "#E2E8F0",
          boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.5)" : "0 8px 30px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              bgcolor: isDark ? "rgba(217,70,239,0.15)" : "rgba(99,102,241,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 1.5,
              color: brandColor,
            }}
          >
            <Lock size={24} />
          </Box>
          <Typography component="h1" sx={{ fontSize: "1.35rem", fontWeight: 800, color: isDark ? "#F8FAFC" : "#0F172A" }}>
            Set New Password
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: isDark ? "#94A3B8" : "#64748B", mt: 0.5 }}>
            Choose a strong password for your support account
          </Typography>
        </Box>

        {success ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2.5, borderRadius: "10px" }}>
              Your password has been reset successfully! You can now sign in with your new password.
            </Alert>
            <Button
              component={RouterLink}
              to={`${base}/login`}
              fullWidth
              variant="contained"
              sx={{ bgcolor: brandColor, borderRadius: "50px", textTransform: "none", fontWeight: 700 }}
            >
              Sign In Now
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: "10px" }}>{error}</Alert>}
            <Stack spacing={2}>
              <TextField
                fullWidth
                required
                type={showPassword ? "text" : "password"}
                label="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock size={16} color={isDark ? "#94A3B8" : "#64748B"} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword((p) => !p)} edge="end">
                        {showPassword ? <EyeOff size={16} color={isDark ? "#94A3B8" : "#64748B"} /> : <Eye size={16} color={isDark ? "#94A3B8" : "#64748B"} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={portalInputSx(isDark)}
              />

              <TextField
                fullWidth
                required
                type={showPassword ? "text" : "password"}
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock size={16} color={isDark ? "#94A3B8" : "#64748B"} />
                    </InputAdornment>
                  ),
                }}
                sx={portalInputSx(isDark)}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={busy}
                sx={{
                  bgcolor: brandColor,
                  borderRadius: "50px",
                  py: 1.2,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { bgcolor: isDark ? "#C026D3" : "#4F46E5" },
                }}
              >
                {busy ? <CircularProgress size={20} color="inherit" /> : "Save New Password"}
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

function PortalAuth() {
  const { slug, setSession, base } = usePortal();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");

  useEffect(() => {
    const token = new URLSearchParams(location.hash.replace(/^#/, "")).get("token");
    window.history.replaceState(null, "", `${base}/auth`);
    if (!token) {
      setError("This sign-in link is incomplete. Please request a new one.");
      return;
    }
    portalApi.session(slug, token)
      .then((s) => {
        setSession({ token: s.token, email: s.email, user: s.user || { email: s.email } });
        navigate(`${base}/tickets`, { replace: true });
      })
      .catch((err) => setError(errText(err, "This sign-in link has expired. Please request a new one.")));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return error ? (
    <Box sx={{ maxWidth: 480, mx: "auto" }}>
      <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      <Button component={RouterLink} to={`${base}/login`}>Go to Sign In</Button>
    </Box>
  ) : <Centered><CircularProgress size={24} /></Centered>;
}

function PortalMyTickets() {
  const { slug, session, setSession, base, config } = usePortal();
  const isDark = (config?.theme || "dark") === "dark";
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!session?.token) return;
    portalApi.myTickets(slug, session.token).then((r) => setRows(r.rows)).catch((err) => {
      if (err?.response?.status === 401) setSession(null);
      else setError(errText(err, "Could not load your tickets"));
    });
  }, [slug, session, setSession]);

  if (!session?.token) return <PortalLogin returnTo={`${base}/tickets`} />;

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2.5 }} spacing={1}>
        <Typography component="h1" sx={{ fontSize: "1.35rem", fontWeight: 800, flexGrow: 1, color: isDark ? "#F8FAFC" : "#0F172A" }}>
          Your Support Tickets
        </Typography>
        <Typography sx={{ fontSize: "0.82rem", color: isDark ? "#94A3B8" : "#64748B", display: { xs: "none", sm: "block" } }}>
          {session.email}
        </Typography>
        <Button size="small" startIcon={<LogOut size={14} />} onClick={() => setSession(null)} sx={{ textTransform: "none" }}>
          Sign out
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          bgcolor: isDark ? "#161226" : "#fff",
          borderColor: isDark ? "#281F47" : "#E2E8F0",
        }}
      >
        {!rows ? (
          <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>
        ) : !rows.length ? (
          <Typography sx={{ p: 4, color: isDark ? "#94A3B8" : "#64748B", textAlign: "center" }}>No tickets found for {session.email}.</Typography>
        ) : (
          rows.map((t, i) => (
            <Box key={t.key}>
              {i > 0 && <Divider sx={{ borderColor: isDark ? "#281F47" : "#F1F5F9" }} />}
              <Link
                component={RouterLink}
                to={`${base}/tickets/${t.key}`}
                underline="none"
                sx={{
                  display: "flex",
                  gap: 1.5,
                  alignItems: "center",
                  p: 2.25,
                  color: "inherit",
                  "&:hover": { bgcolor: isDark ? "#1E1838" : "#F8FAFC" },
                }}
              >
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography noWrap sx={{ fontWeight: 700, color: isDark ? "#F8FAFC" : "#0F172A" }}>{t.subject}</Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: isDark ? "#94A3B8" : "#64748B", mt: 0.25 }}>
                    #{t.key} · Updated {fmt(t.updatedAt)}
                  </Typography>
                </Box>
                <StatusChip status={t.status} statusType={t.statusType} />
              </Link>
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
}

function PortalTicket() {
  const { slug, session, setSession, config, base } = usePortal();
  const { key } = useParams();
  const isDark = (config?.theme || "dark") === "dark";
  const [t, setT] = useState(null);
  const [error, setError] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!session?.token) return;
    portalApi.myTicket(slug, session.token, key).then(setT).catch((err) => {
      if (err?.response?.status === 401) setSession(null);
      else setError(errText(err, "Ticket not found"));
    });
  }, [slug, session, setSession, key]);

  useEffect(() => {
    load();
  }, [load]);

  if (!session?.token) return <PortalLogin returnTo={`${base}/tickets/${key}`} />;

  const download = async (f) => {
    try {
      const blob = await portalApi.file(slug, session.token, key, f._id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      setError("Could not download file.");
    }
  };

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      setT(await portalApi.reply(slug, session.token, key, reply));
      setReply("");
    } catch (err) {
      setError(errText(err, "Could not send your reply"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <BackLink to={`${base}/tickets`}>All tickets</BackLink>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!t ? (!error && <CircularProgress size={24} />) : (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: "18px",
            bgcolor: isDark ? "#161226" : "#fff",
            borderColor: isDark ? "#281F47" : "#E2E8F0",
          }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2.5 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography component="h1" sx={{ fontSize: "1.35rem", fontWeight: 800, wordBreak: "break-word", color: isDark ? "#F8FAFC" : "#0F172A" }}>
                {t.subject}
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: isDark ? "#94A3B8" : "#64748B", mt: 0.25 }}>
                #{t.key} · Opened {fmt(t.createdAt)}
              </Typography>
            </Box>
            <StatusChip status={t.status} statusType={t.statusType} />
          </Stack>

          <Stack spacing={2} sx={{ mb: 3 }}>
            {t.messages?.map((m) => (
              <Box key={m._id} sx={{ alignSelf: m.from === "you" ? "flex-end" : "flex-start", maxWidth: { xs: "100%", sm: "85%" } }}>
                <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#94A3B8" : "#64748B", mb: 0.5, textAlign: m.from === "you" ? "right" : "left" }}>
                  {m.from === "you" ? "You" : m.name || "Support"} · {fmt(m.createdAt)}
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "14px",
                    bgcolor: m.from === "you" ? (isDark ? "#281D45" : "#EEF4FF") : isDark ? "#1B1530" : "#F8FAFC",
                    border: isDark ? "1px solid #36295C" : "1px solid #E2E8F0",
                    color: isDark ? "#F8FAFC" : "#101828",
                    fontSize: "0.9rem",
                    lineHeight: 1.6,
                    wordBreak: "break-word",
                  }}
                >
                  {m.html ? <Box dangerouslySetInnerHTML={{ __html: m.html }} /> : <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "0.9rem" }}>{m.content}</Typography>}
                  {m.attachments?.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                      {m.attachments.map((f) => (
                        <Chip key={f._id} size="small" icon={<Paperclip size={12} />} label={`${f.fileName} (${fileSize(f.size)})`} onClick={() => download(f)} />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>

          {config.allowCustomerReplies && (
            <Box sx={{ mt: 3, pt: 2, borderTop: isDark ? "1px solid #281F47" : "1px solid #E2E8F0" }}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                placeholder="Send a reply…"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: isDark ? "#120E22" : "#fff",
                    color: isDark ? "#fff" : "inherit",
                    borderRadius: "12px",
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={send}
                disabled={busy || !reply.trim()}
                sx={{
                  mt: 1.5,
                  bgcolor: "#D946EF",
                  borderRadius: "50px",
                  textTransform: "none",
                  fontWeight: 700,
                  px: 3,
                  "&:hover": { bgcolor: "#C026D3" },
                }}
                endIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Send size={15} />}
              >
                {busy ? "Sending…" : "Send Reply"}
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
const PAGES = {
  home: PortalHome,
  article: PortalArticle,
  new: PortalNewTicket,
  tickets: PortalMyTickets,
  ticket: PortalTicket,
  "guest-ticket": PortalGuestTicket,
  "guest-ticket-direct": PortalGuestTicket,
  auth: PortalAuth,
  login: PortalLogin,
  register: PortalRegister,
  "forgot-password": PortalForgotPassword,
  "reset-password": PortalResetPassword,
};

export default function Portal({ page = "home" }) {
  const Page = PAGES[page] || PortalHome;
  return (
    <PortalShell>
      <Page />
    </PortalShell>
  );
}
